import { useEffect, useRef, useState } from 'react'

import {
  faPen,
  faTrash,
  faClose,
  faCheck,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  AnalyticsEntityType,
  AnalyticsSource,
  Button,
  Input,
} from '@b-stock/fe-core/bstock-react'
import type { SavedSearchDto } from '@b-stock/saved-search-api-client'

import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { useSavedSearch } from './SavedSearchContext'
import {
  CancelButton,
  EditButton,
  SaveButton,
  SavedSearchItemChip,
  SavedSearchItemChips,
  SavedSearchItemContent,
  SavedSearchItemName,
  SavedSearchLabel,
  SavedSearchLi,
} from './ui'
import useSavedSearchId from './useSavedSearchId'

type SavedSearchItem = {
  selected: boolean
  setSelected: React.Dispatch<
    React.SetStateAction<SavedSearchDto['_id'] | null>
  >
  isHomePage?: boolean
} & SavedSearchDto

const DELETE_DELAY = 500 // ms
const EDIT_HEIGHT = '7.375rem'

const SavedSearchItem = ({
  _id,
  displayFilters,
  name,
  selected,
  setSelected,
  isHomePage = false,
}: SavedSearchItem) => {
  const { savedSearchId, setSavedSearchId } = useSavedSearchId()
  const { deleteSavedSearch, updateSavedSearchName } = useSavedSearch()
  const { trackButtonClicked } = useAnalytics()

  const [inputValue, setInputValue] = useState(name)
  const [inputError, setInputError] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [height, setHeight] = useState('auto')

  const itemRef = useRef<HTMLLIElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (itemRef.current) {
      setHeight(`${itemRef.current.scrollHeight}px`)
    }
  }, [])

  const toggleEdit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()

    if (isEditing) {
      setIsEditing(false)
      setInputValue(name)
    } else {
      if (isHomePage) {
        trackButtonClicked({
          screenName: 'homepage',
          buttonName: 'edit_save_search',
          source: AnalyticsSource.HOME_PORTAL,
          entityType: AnalyticsEntityType.BUYER,
        })
      }
      setIsEditing(true)
      setHeight(EDIT_HEIGHT)
    }
  }

  useEffect(() => {
    const focusTimer = setTimeout(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus()
      }
    }, 100)

    let heightTimer: ReturnType<typeof setTimeout> | undefined

    // quickly set the pixel height so that the delete collapse transitions
    if (!isEditing) {
      setHeight('auto') // oxlint-disable-line react/set-state-in-effect
      heightTimer = setTimeout(
        () => setHeight(`${itemRef.current?.scrollHeight}px`),
        100
      )
    }

    return () => {
      clearTimeout(focusTimer)
      if (heightTimer) clearTimeout(heightTimer)
    }
  }, [isEditing])

  const handleSubmit = async (event: React.SubmitEvent) => {
    event.preventDefault()

    if (!inputValue) {
      setInputError(true)
      return
    }

    if (isHomePage) {
      trackButtonClicked({
        screenName: 'homepage',
        buttonName: 'update_save_search',
        source: AnalyticsSource.HOME_PORTAL,
        entityType: AnalyticsEntityType.BUYER,
        search_term: displayFilters.join(', '),
        previous_search_name: name,
        search_name: inputValue,
      })
    }

    setInputError(false)
    await updateSavedSearchName(_id, inputValue)
    setIsEditing(false)
  }

  const handleChange = () => setSelected((prev) => (prev === _id ? null : _id))

  const handleDelete = () => {
    if (isHomePage) {
      trackButtonClicked({
        screenName: 'homepage',
        buttonName: 'delete_saved_search',
        source: AnalyticsSource.HOME_PORTAL,
        entityType: AnalyticsEntityType.BUYER,
        search_term: name,
      })
    }

    if (selected) setSelected(null)
    if (savedSearchId === _id) setSavedSearchId()

    setIsDeleting(true)
    setTimeout(async () => await deleteSavedSearch(_id), DELETE_DELAY)
  }

  return (
    <SavedSearchLi $height={height} $shouldCollapse={isDeleting} ref={itemRef}>
      <SavedSearchLabel htmlFor={_id}>
        <input
          id={_id}
          type="checkbox"
          name="savedSearch"
          checked={selected}
          onChange={handleChange}
        />

        <SavedSearchItemContent>
          <SavedSearchItemName
            as={isEditing ? 'form' : undefined}
            onSubmit={handleSubmit}
          >
            {isEditing ? (
              <>
                <Input
                  data-testid="savedSearchNameInput"
                  ref={inputRef}
                  error={inputError}
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                />

                <CancelButton aria-label="Cancel" onClick={toggleEdit}>
                  <FontAwesomeIcon icon={faClose} />
                </CancelButton>

                <SaveButton aria-label="Save" disabled={inputValue === name}>
                  <FontAwesomeIcon icon={faCheck} />
                </SaveButton>
              </>
            ) : (
              <>
                <p>{name}</p>

                <EditButton aria-label="Edit name" onClick={toggleEdit}>
                  <FontAwesomeIcon icon={faPen} />
                </EditButton>
              </>
            )}
          </SavedSearchItemName>

          <SavedSearchItemChips>
            {displayFilters.map((string) => (
              <SavedSearchItemChip key={string}>{string}</SavedSearchItemChip>
            ))}
          </SavedSearchItemChips>
        </SavedSearchItemContent>

        <Button
          aria-label="Delete"
          appearance="plainTertiary"
          type="button"
          onClick={handleDelete}
        >
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      </SavedSearchLabel>
    </SavedSearchLi>
  )
}

export default SavedSearchItem
